import React, { useState, useEffect, useCallback } from "react";
import PageWrapperForm from "../../components/PageWrapperForm";
import { useSession } from "hooks/useSession";
import { fetchWrapper } from "utils/apiFetch";

const API_BASE = "https://pro.mokshiq.in";
const PAGE_SIZE = 50;

/*
 * Messaging charges — SMS and WhatsApp on one ledger.
 *
 * SCOPING IS THE SERVER'S JOB
 * ---------------------------
 *   Super Admin  -> every row
 *   Main Admin   -> rows where main_admin is them, plus a per-admin breakdown
 *   Admin        -> rows where they are the account actually debited
 *   anyone else  -> 403
 *
 * The admin_id filter is honoured only for Super Admin and Main Admin, and for
 * a Main Admin it is intersected with their own scope, so passing an outsider's
 * id returns nothing rather than another company's spend. Nothing here decides
 * visibility client-side.
 *
 * WHAT IS DELIBERATELY NOT SHOWN
 * ------------------------------
 * The message body. Every OTP travels inside one, and this screen is readable
 * by admins, so only the length is stored server-side. Recipient numbers are
 * masked at write time (******3210) and cannot be un-masked here.
 */

const money = (n) => `₹${Number(n || 0).toFixed(2)}`;

function todayISO(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

function StatCard({ label, value, sub, tone = "primary" }) {
  return (
    <div className="col-6 col-lg-3">
      <div className="card shadow-sm h-100">
        <div className="card-body py-3">
          <div className="text-muted small text-uppercase" style={{ letterSpacing: ".06em" }}>
            {label}
          </div>
          <div className={`fw-bold text-${tone}`} style={{ fontSize: "1.45rem", lineHeight: 1.25 }}>
            {value}
          </div>
          {sub && <div className="text-muted small">{sub}</div>}
        </div>
      </div>
    </div>
  );
}

function ChannelBadge({ channel }) {
  const wa = channel === "whatsapp";
  return (
    <span className={`badge ${wa ? "bg-success-subtle text-success" : "bg-info-subtle text-info"}`}>
      {wa ? "WhatsApp" : "SMS"}
    </span>
  );
}

// A row can be uncharged for several distinct reasons and they mean very
// different things operationally, so each gets its own wording.
const REASON_LABEL = {
  send_failed: "Gateway rejected",
  admin_unresolved: "No admin resolved",
  billing_disabled: "Billing off",
  billing_error: "Billing error",
};

const SmsChargesPage = () => {
  const { role } = useSession();

  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [rowsLoading, setRowsLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  const [startdate, setStartdate] = useState(todayISO(-29));
  const [enddate, setEnddate] = useState(todayISO());
  const [channel, setChannel] = useState("");
  const [only, setOnly] = useState("");
  const [adminId, setAdminId] = useState("");

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [toast, setToast] = useState(null);

  const showToast = useCallback((msg, isError = false) => {
    setToast({ msg, isError });
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(() => setToast(null), 3200);
  }, []);

  const canFilterByAdmin = role === "Super Admin" || role === "Main Admin";

  const commonParams = useCallback(() => {
    const p = new URLSearchParams();
    if (startdate && enddate) {
      p.set("startdate", startdate);
      p.set("enddate", enddate);
    }
    if (adminId) p.set("admin_id", adminId);
    return p;
  }, [startdate, enddate, adminId]);

  // ── summary ─────────────────────────────────────────────────────────────
  const loadSummary = useCallback(async () => {
    try {
      const res = await fetchWrapper(`${API_BASE}/sms_charge_summary?${commonParams()}`);
      if (res?.status) {
        setForbidden(false);
        setSummary(res.data);
      } else {
        if (/permitted/i.test(res?.message || "")) setForbidden(true);
      }
    } catch (e) {
      console.error("sms summary failed", e);
    }
  }, [commonParams]);

  // ── rows ────────────────────────────────────────────────────────────────
  const loadRows = useCallback(async () => {
    setRowsLoading(true);
    try {
      const p = commonParams();
      if (channel) p.set("channel", channel);
      if (only) p.set("only", only);
      p.set("page", String(page));
      p.set("page_size", String(PAGE_SIZE));

      const res = await fetchWrapper(`${API_BASE}/sms_charge_logs?${p.toString()}`);
      if (res?.status) {
        setForbidden(false);
        setRows(res.data || []);
        setTotalPages(res.pages || 1);
        setTotalCount(res.count || 0);
      } else {
        if (/permitted/i.test(res?.message || "")) setForbidden(true);
        else showToast(res?.message || "Failed to load charges", true);
        setRows([]);
      }
    } catch (e) {
      showToast("Error connecting to server", true);
      setRows([]);
    } finally {
      setRowsLoading(false);
    }
  }, [commonParams, channel, only, page, showToast]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadRows(); }, [loadRows]);
  useEffect(() => { setPage(1); }, [startdate, enddate, channel, only, adminId]);

  if (forbidden) {
    return (
    <main>
      <div className="container-fluid">
        <div className="row m-1 mb-4">
          <div className="col-12">
            <div className="alert alert-warning m-3">
              Your role does not have access to messaging charges.
            </div>
          </div>
        </div>
      </div>
    </main>
    );
  }

  const rangeTotals = summary?.selected_range;
  const byChannel = summary?.by_channel;
  const rates = summary?.rates;

  return (
    <main>
      <div className="container-fluid">
        {/* Custom Header Layout */}
        <div className="row m-1 mb-4">
          <div className="col-12 d-flex justify-content-between align-items-center">
            <div>
              <h4 className="main-title fw-bold" style={{ color: "#1a1a1a", fontSize: "1.8rem", marginBottom: "4px" }}>Messaging Charges</h4>
              <div className="d-flex align-items-center gap-2 mb-0" style={{ fontSize: "13.5px", fontWeight: "500" }}>
                <a href="#" className="text-decoration-none" style={{ color: "#7e57c2", display: "flex", alignItems: "center" }}>
                  <i className="ph-duotone ph-list-dashes f-s-16"></i> <span className="ms-1">Messaging</span>
                </a>
                <span style={{ color: "#a0a5b1" }}>/</span>
                <a href="#" className="text-decoration-none" style={{ color: "#7e57c2" }}>
                  Charges
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="row m-1">
          <div className="col-12">
        {/* Totals */}
        <div className="row g-3 mb-4">
          <StatCard
            label="Selected range"
            value={money(rangeTotals?.amount)}
            sub={`${rangeTotals?.billed_sends ?? 0} billed of ${rangeTotals?.sends ?? 0} sends`}
          />
          <StatCard
            label="Today"
            value={money(summary?.today?.amount)}
            sub={`${summary?.today?.billed_sends ?? 0} billed sends`}
            tone="dark"
          />
          <StatCard
            label="This month"
            value={money(summary?.this_month?.amount)}
            sub={`${summary?.this_month?.billed_sends ?? 0} billed sends`}
            tone="dark"
          />
          <StatCard
            label="All time"
            value={money(summary?.all_time?.amount)}
            sub={`${summary?.all_time?.billed_sends ?? 0} billed sends`}
            tone="dark"
          />
        </div>

        {/* Channel split + health */}
        <div className="row g-3 mb-4">
          <div className="col-12 col-lg-6">
            <div className="card shadow-sm h-100">
              <div className="card-body">
                <h6 className="card-title mb-3">By channel — selected range</h6>
                <div className="d-flex gap-4 flex-wrap">
                  <div>
                    <ChannelBadge channel="sms" />
                    <div className="fw-bold mt-1" style={{ fontSize: "1.2rem" }}>
                      {money(byChannel?.sms?.amount)}
                    </div>
                    <div className="text-muted small">
                      {byChannel?.sms?.billed_sends ?? 0} sends
                      {rates && ` @ ${money(rates.sms)}`}
                    </div>
                  </div>
                  <div>
                    <ChannelBadge channel="whatsapp" />
                    <div className="fw-bold mt-1" style={{ fontSize: "1.2rem" }}>
                      {money(byChannel?.whatsapp?.amount)}
                    </div>
                    <div className="text-muted small">
                      {byChannel?.whatsapp?.billed_sends ?? 0} sends
                      {rates && ` @ ${money(rates.whatsapp)}`}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-6">
            <div className="card shadow-sm h-100">
              <div className="card-body">
                <h6 className="card-title mb-3">Needs attention</h6>
                <div className="d-flex gap-2 flex-wrap">
                  <button
                    type="button"
                    className={`btn btn-sm ${only === "failed" ? "btn-danger" : "btn-outline-danger"}`}
                    onClick={() => setOnly(only === "failed" ? "" : "failed")}
                  >
                    {summary?.failed_sends ?? 0} gateway rejects
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${only === "negative" ? "btn-warning" : "btn-outline-warning"}`}
                    onClick={() => setOnly(only === "negative" ? "" : "negative")}
                  >
                    {summary?.negative_balance_sends ?? 0} sent on a negative wallet
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${only === "unbilled" ? "btn-secondary" : "btn-outline-secondary"}`}
                    onClick={() => setOnly(only === "unbilled" ? "" : "unbilled")}
                  >
                    {summary?.unbilled_unresolved ?? 0} unbilled, no admin
                  </button>
                </div>
                <div className="text-muted small mt-2">
                  A reject is not charged — the gateway does not bill for a
                  submission it refused. “No admin” means the number belongs to
                  nobody on the platform, so the send went out unbilled.
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Per-admin breakdown (Main Admin / Super Admin only) */}
        {summary?.by_admin?.length > 0 && (
          <div className="card shadow-sm mb-4">
            <div className="card-body">
              <h6 className="card-title mb-3">Spend by admin — selected range</h6>
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Admin</th>
                      <th>Phone</th>
                      <th className="text-end">Billed sends</th>
                      <th className="text-end">Amount</th>
                      <th className="text-end">Filter</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.by_admin.map((a) => (
                      <tr key={a.admin_id}>
                        <td className="fw-semibold">{a.name || "—"}</td>
                        <td className="text-muted small">{a.phone || "—"}</td>
                        <td className="text-end">{a.sends}</td>
                        <td className="text-end fw-semibold">{money(a.amount)}</td>
                        <td className="text-end">
                          <button
                            type="button"
                            className={`btn btn-sm ${
                              String(adminId) === String(a.admin_id)
                                ? "btn-primary"
                                : "btn-outline-primary"
                            }`}
                            onClick={() =>
                              setAdminId(
                                String(adminId) === String(a.admin_id) ? "" : String(a.admin_id)
                              )
                            }
                          >
                            {String(adminId) === String(a.admin_id) ? "Clear" : "Only this"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="card shadow-sm mb-4">
          <div className="card-body">
            <div className="row g-3 align-items-end">
              <div className="col-6 col-md-3">
                <label className="form-label">From</label>
                <input
                  type="date"
                  className="form-control"
                  value={startdate}
                  max={enddate}
                  onChange={(e) => setStartdate(e.target.value)}
                />
              </div>
              <div className="col-6 col-md-3">
                <label className="form-label">To</label>
                <input
                  type="date"
                  className="form-control"
                  value={enddate}
                  min={startdate}
                  max={todayISO()}
                  onChange={(e) => setEnddate(e.target.value)}
                />
              </div>
              <div className="col-6 col-md-2">
                <label className="form-label">Channel</label>
                <select
                  className="form-select"
                  value={channel}
                  onChange={(e) => setChannel(e.target.value)}
                >
                  <option value="">All</option>
                  <option value="sms">SMS</option>
                  <option value="whatsapp">WhatsApp</option>
                </select>
              </div>
              <div className="col-6 col-md-2">
                <label className="form-label">Show</label>
                <select
                  className="form-select"
                  value={only}
                  onChange={(e) => setOnly(e.target.value)}
                >
                  <option value="">Everything</option>
                  <option value="failed">Gateway rejects</option>
                  <option value="unbilled">Unbilled</option>
                  <option value="negative">Negative wallet</option>
                </select>
              </div>
              <div className="col-12 col-md-2 text-md-end">
                <span className="badge bg-primary-subtle text-primary px-3 py-2">
                  {totalCount} record{totalCount === 1 ? "" : "s"}
                </span>
                {canFilterByAdmin && adminId && (
                  <button
                    type="button"
                    className="btn btn-sm btn-link d-block ms-auto p-0 mt-1"
                    onClick={() => setAdminId("")}
                  >
                    Clear admin filter
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Ledger */}
        <div className="card shadow-sm">
          <div className="card-body p-0">
            {rowsLoading ? (
              <div className="d-flex justify-content-center py-5">
                <div className="spinner-border text-primary" role="status" />
              </div>
            ) : rows.length === 0 ? (
              <div className="alert alert-light text-muted text-center m-3 mb-0">
                No messages in this range.
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Date</th>
                      <th>Channel</th>
                      <th>To</th>
                      <th>Recipient</th>
                      {canFilterByAdmin && <th>Billed to</th>}
                      <th>Template</th>
                      <th className="text-end">Chars</th>
                      <th className="text-end">Amount</th>
                      <th>Status</th>
                      <th className="text-end">Opening</th>
                      <th className="text-end">Closing</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id} className={r.negative_balance ? "table-warning" : undefined}>
                        <td className="text-muted small text-nowrap">{r.date}</td>
                        <td><ChannelBadge channel={r.channel} /></td>
                        <td className="font-monospace small">{r.phone || "—"}</td>
                        <td>{r.recipient || <span className="text-muted small">unknown</span>}</td>
                        {canFilterByAdmin && (
                          <td className="small">{r.admin || <span className="text-muted">—</span>}</td>
                        )}
                        <td className="text-muted small font-monospace">{r.purpose || "—"}</td>
                        <td className="text-end text-muted small">{r.length}</td>
                        <td className="text-end fw-semibold">
                          {r.charged ? money(r.amount) : <span className="text-muted">—</span>}
                        </td>
                        <td className="text-nowrap">
                          {!r.sent ? (
                            <span className="badge bg-danger-subtle text-danger">
                              {REASON_LABEL[r.reason] || "Failed"}
                            </span>
                          ) : r.charged ? (
                            <span className="badge bg-success-subtle text-success">Billed</span>
                          ) : (
                            <span
                              className="badge bg-secondary-subtle text-secondary"
                              title={r.reason}
                            >
                              {REASON_LABEL[r.reason] || "Not billed"}
                            </span>
                          )}
                          {r.negative_balance && (
                            <span className="badge bg-warning-subtle text-warning ms-1">
                              Negative
                            </span>
                          )}
                        </td>
                        <td className="text-end text-muted small">
                          {r.opening_balance == null ? "—" : money(r.opening_balance)}
                        </td>
                        <td
                          className={`text-end small ${
                            r.closing_balance != null && r.closing_balance < 0
                              ? "text-danger fw-semibold"
                              : "text-muted"
                          }`}
                        >
                          {r.closing_balance == null ? "—" : money(r.closing_balance)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Pagination */}
        {rows.length > 0 && (
          <div className="d-flex justify-content-between align-items-center mt-3">
            <span className="text-muted small">
              Page {page} of {totalPages}
            </span>
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary px-4 py-2"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary px-4 py-2"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </div>
          </div>
        )}

        <p className="text-muted small mt-3 mb-0">
          Amounts here are the authoritative billed total. Wallet balances are
          stored as floating point, so the wallet itself drifts by fractions of a
          paisa over very large volumes — reconcile against this ledger, not
          against the wallet delta.
        </p>
          </div>
        </div>
      {toast && (
        <div
          className={`position-fixed bottom-0 start-50 translate-middle-x mb-4 px-4 py-2 rounded text-white ${
            toast.isError ? "bg-danger" : "bg-success"
          }`}
          style={{ zIndex: 1080 }}
        >
          {toast.msg}
        </div>
      )}
      </div>
    </main>
  );
};

export default SmsChargesPage;